import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	IconButton,
	InputAdornment,
	Alert,
	Box,
	Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";

const ChangePassword = ({ open, onClose }) => {
	const [formData, setFormData] = useState({
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});

	const [showPassword, setShowPassword] = useState({
		current: false,
		new: false,
		confirm: false,
	});

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	const validateForm = () => {
		const newErrors = {};

		if (!formData.currentPassword) {
			newErrors.currentPassword = "Current password is required";
		}

		if (!formData.newPassword) {
			newErrors.newPassword = "New password is required";
		} else if (formData.newPassword.length < 8) {
			newErrors.newPassword = "Password must be at least 8 characters";
		}

		if (!formData.confirmPassword) {
			newErrors.confirmPassword = "Please confirm your new password";
		} else if (formData.newPassword !== formData.confirmPassword) {
			newErrors.confirmPassword = "Passwords do not match";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setLoading(true);

		try {
			const token = localStorage.getItem("parallextoken");
			const response = await axios.post(
				"https://parallexsms.approot.ng/change_password.php",
				{
					currentPassword: formData.currentPassword,
					newPassword: formData.newPassword,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data.success) {
				toast.success("Password changed successfully!");
				resetForm();
				onClose();
			} else {
				setErrors({ submit: response.data.message });
			}
		} catch (error) {
			console.error("Password change error:", error);
			setErrors({ submit: "Failed to change password. Please try again." });
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setFormData({
			currentPassword: "",
			newPassword: "",
			confirmPassword: "",
		});
		setErrors({});
	};

	const handleChange = (field) => (e) => {
		const value = e.target.value;
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error for this field
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
		if (errors.submit) {
			setErrors((prev) => ({
				...prev,
				submit: "",
			}));
		}
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const togglePasswordVisibility = (field) => {
		setShowPassword((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
				<Box display='flex' alignItems='center' gap={2}>
					<Lock sx={{ color: "#DB9E27" }} />
					<Typography variant='h6' fontWeight='bold'>
						Change Password
					</Typography>
				</Box>
			</DialogTitle>

			<form onSubmit={handleSubmit}>
				<DialogContent sx={{ pt: 3 }}>
					{errors.submit && (
						<Alert severity='error' sx={{ mb: 3 }}>
							{errors.submit}
						</Alert>
					)}

					<TextField
						fullWidth
						label='Current Password'
						type={showPassword.current ? "text" : "password"}
						value={formData.currentPassword}
						onChange={handleChange("currentPassword")}
						error={!!errors.currentPassword}
						helperText={errors.currentPassword}
						margin='normal'
						required
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										onClick={() => togglePasswordVisibility("current")}
										edge='end'
									>
										{showPassword.current ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<TextField
						fullWidth
						label='New Password'
						type={showPassword.new ? "text" : "password"}
						value={formData.newPassword}
						onChange={handleChange("newPassword")}
						error={!!errors.newPassword}
						helperText={errors.newPassword || "Minimum 8 characters"}
						margin='normal'
						required
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										onClick={() => togglePasswordVisibility("new")}
										edge='end'
									>
										{showPassword.new ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<TextField
						fullWidth
						label='Confirm New Password'
						type={showPassword.confirm ? "text" : "password"}
						value={formData.confirmPassword}
						onChange={handleChange("confirmPassword")}
						error={!!errors.confirmPassword}
						helperText={errors.confirmPassword}
						margin='normal'
						required
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										onClick={() => togglePasswordVisibility("confirm")}
										edge='end'
									>
										{showPassword.confirm ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<Alert severity='info' sx={{ mt: 3 }}>
						<Typography variant='body2'>
							• Use a strong password with at least 8 characters
							<br />
							• Include uppercase, lowercase, numbers, and symbols
							<br />• Don't reuse old passwords
						</Typography>
					</Alert>
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						type='submit'
						variant='contained'
						disabled={loading}
						sx={{
							backgroundColor: "#DB9E27",
							"&:hover": {
								backgroundColor: "#c5891e",
							},
						}}
					>
						{loading ? "Changing Password..." : "Change Password"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default ChangePassword;
